/* Unravel — thermal receipt page.
 *
 * Reads an order payload from the `r` query param (URL-encoded JSON), written by
 * the thank-you page checkout UI extension, and prints it line by line.
 *
 * SECURITY NOTE: everything in that payload is attacker-controllable — it is just
 * a URL. So every value is escaped before it touches innerHTML, every number is
 * coerced, and every list is length-capped. Nothing here is trusted, and nothing
 * here is authoritative: this page is a display copy, not a tax document.
 */
(function () {
  'use strict';

  var MAX_LINES = 40;
  var MAX_TEXT = 80;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function clampText(s) {
    var v = String(s == null ? '' : s);
    return v.length > MAX_TEXT ? v.slice(0, MAX_TEXT - 1) + '…' : v;
  }

  function num(n) {
    var v = Number(n);
    return isFinite(v) ? v : 0;
  }

  function money(amount, currency) {
    var v = num(amount);
    var cur = /^[A-Z]{3}$/.test(String(currency)) ? String(currency) : 'INR';
    try {
      return new Intl.NumberFormat(cur === 'INR' ? 'en-IN' : undefined, {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: 2,
      })
        .format(v)
        .replace('-', '−');
    } catch (e) {
      return (v < 0 ? '−' : '') + cur + ' ' + Math.abs(v).toFixed(2);
    }
  }

  function receiptDate(iso) {
    var d = iso ? new Date(iso) : new Date();
    if (isNaN(d.getTime())) d = new Date();
    var M = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    var p = function (x) { return (x < 10 ? '0' : '') + x; };
    return p(d.getDate()) + ' ' + M[d.getMonth()] + ' ' + d.getFullYear() +
      ' · ' + p(d.getHours()) + ':' + p(d.getMinutes());
  }

  function row(left, right, cls) {
    return {
      c: 'ur-r' + (cls ? ' ' + cls : ''),
      html: '<span>' + esc(clampText(left)) + '</span>' +
            '<span class="ur-right">' + esc(clampText(right)) + '</span>',
    };
  }

  function buildLines(o) {
    var cur = o.currency;
    var L = [];

    L.push({ c: 'ur-pad' });
    L.push({ c: 'ur-logo', html: '<b>✦</b>' });
    L.push({ c: 'ur-c ur-strong', html: esc(clampText(o.shopName)) });
    if (o.shopDomain) L.push({ c: 'ur-c ur-dim', html: esc(clampText(o.shopDomain)) });
    L.push({ c: 'ur-dash' });

    (o.lines || []).slice(0, MAX_LINES).forEach(function (li) {
      L.push(row(String(li.title || 'Item').toUpperCase(), money(li.amount, cur)));
      var bits = [];
      if (num(li.qty) > 1) bits.push(num(li.qty) + ' ×');
      if (li.variant) bits.push(li.variant);
      if (bits.length) {
        L.push({
          c: 'ur-r ur-dim',
          html: '<span>&nbsp;&nbsp;' + esc(clampText(bits.join(' '))) + '</span><span class="ur-right"></span>',
        });
      }
    });

    L.push({ c: 'ur-dash' });
    if (o.subtotal != null) L.push(row('Subtotal', money(o.subtotal, cur)));
    if (o.tax != null && num(o.tax) !== 0) L.push(row(o.taxLabel || 'Tax', money(o.tax, cur)));
    if (o.shipping != null) {
      L.push(row('Shipping', num(o.shipping) === 0 ? 'FREE' : money(o.shipping, cur), 'ur-dim'));
    }
    (o.discounts || []).slice(0, 10).forEach(function (d) {
      L.push(row(d.code || 'Discount', money(-Math.abs(num(d.amount)), cur), 'ur-dim'));
    });

    L.push({ c: 'ur-pad' });
    L.push(row('TOTAL PAID', money(o.total, cur), 'ur-big'));
    L.push({ c: 'ur-dash' });

    if (o.orderName) L.push(row('Order', o.orderName, 'ur-dim'));
    if (o.payment) L.push(row('Paid with', o.payment, 'ur-dim'));
    L.push(row('Date', receiptDate(o.createdAt), 'ur-dim'));

    var code = String(o.orderName || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 24);
    L.push({ barcode: true, seed: code, c: '' });
    if (code) L.push({ c: 'ur-c ur-dim', html: esc(code) });

    L.push({ c: 'ur-pad' });
    L.push({ c: 'ur-c', html: esc(clampText(o.footer)) });
    L.push({ c: 'ur-pad' });
    return L;
  }

  /* Deterministic bars, so the same order always reprints identically. */
  function buildBarcode(seed) {
    var wrap = document.createElement('div');
    wrap.className = 'ur-bcode';
    var h = 2166136261;
    var s = String(seed || 'UNRAVEL');
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
    for (var b = 0; b < 46; b++) {
      h ^= h << 13; h >>>= 0; h ^= h >> 17; h ^= h << 5; h >>>= 0;
      var bar = document.createElement('i');
      var r = (h % 100) / 100;
      bar.style.width = (r < 0.3 ? 3 : r < 0.5 ? 2 : 1) + 'px';
      bar.style.opacity = r < 0.12 ? '0.35' : '1';
      wrap.appendChild(bar);
    }
    return wrap;
  }

  function normalise(raw, defaults) {
    var o = raw && typeof raw === 'object' ? raw : {};
    return {
      shopName: clampText(o.shopName || defaults.shopName),
      shopDomain: clampText(o.shopDomain || defaults.shopDomain),
      orderName: clampText(o.orderName || ''),
      createdAt: o.createdAt || null,
      currency: o.currency || defaults.currency,
      lines: Array.isArray(o.lines) ? o.lines.slice(0, MAX_LINES).map(function (li) {
        return {
          title: clampText(li && li.title),
          variant: clampText(li && li.variant),
          qty: num(li && li.qty),
          amount: num(li && li.amount),
        };
      }) : [],
      subtotal: o.subtotal == null ? null : num(o.subtotal),
      tax: o.tax == null ? null : num(o.tax),
      taxLabel: clampText(o.taxLabel || ''),
      shipping: o.shipping == null ? null : num(o.shipping),
      discounts: Array.isArray(o.discounts) ? o.discounts.slice(0, 10).map(function (d) {
        return { code: clampText(d && d.code), amount: num(d && d.amount) };
      }) : [],
      total: num(o.total),
      payment: clampText(o.payment || ''),
      footer: clampText(o.footer || defaults.footer),
    };
  }

  function readPayload() {
    try {
      var raw = new URLSearchParams(window.location.search).get('r');
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /* Feed the receipt out of the slot.
   *
   * The whole receipt is built and measured ONCE, then slid into view with a single
   * `transform` animation. The earlier version animated each line's height in turn,
   * which forced a layout per line — visually steppy even at a solid 60fps. Transform
   * is composited, so the feed is continuous and the main thread stays free.
   */
  async function print(root, order, opts) {
    var printer = root.querySelector('.ur-printer');
    var win = root.querySelector('.ur-window');
    var feed = root.querySelector('.ur-feed');
    var paper = root.querySelector('.ur-paper');
    var tear = root.querySelector('.ur-tear');
    if (!printer || !win || !feed || !paper) return;

    var speed = opts.speed || 1;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (feed.__anim) { feed.__anim.cancel(); feed.__anim = null; }

    // Build the entire receipt off-document, then attach in one write.
    var frag = document.createDocumentFragment();
    buildLines(order).forEach(function (spec) {
      var ln = document.createElement('div');
      ln.className = 'ur-ln';
      var inner = document.createElement('span');
      if (spec.c) inner.className = spec.c;
      if (spec.barcode) inner.appendChild(buildBarcode(spec.seed));
      else if (spec.html) inner.innerHTML = spec.html;
      else inner.innerHTML = '&nbsp;';
      ln.appendChild(inner);
      frag.appendChild(ln);
    });
    paper.innerHTML = '';
    paper.appendChild(frag);
    if (tear) tear.classList.add('is-on');

    // The one and only forced layout read.
    var h = feed.offsetHeight;
    win.style.height = h + 'px';

    if (reduce) {
      feed.style.transform = 'translateY(0)';
      return;
    }

    feed.style.transform = 'translateY(-100%)';

    // Constant feed rate: a longer receipt takes longer to print rather than
    // racing past at the same duration.
    var duration = Math.min(3800, Math.max(1000, h * 4)) / speed;

    printer.classList.add('is-printing');
    var anim = feed.animate(
      [{ transform: 'translateY(-100%)' }, { transform: 'translateY(0)' }],
      { duration: duration, easing: 'linear' }
    );
    feed.__anim = anim;

    try {
      await anim.finished;
    } catch (e) {
      return; // cancelled by a replay
    }
    feed.style.transform = 'translateY(0)';
    feed.__anim = null;
    printer.classList.remove('is-printing');
  }

  function init(root) {
    if (root.dataset.urReady === '1') return;
    root.dataset.urReady = '1';

    var defaults = {
      shopName: root.dataset.shopName || 'UNRAVEL',
      shopDomain: root.dataset.shopDomain || '',
      currency: root.dataset.currency || 'INR',
      footer: root.dataset.footer || 'THANK YOU',
    };
    var speed = parseFloat(root.dataset.speed) || 1;

    var payload = readPayload();
    var empty = root.querySelector('.ur-empty');
    var printerWrap = root.querySelector('.ur-printer');
    var actions = root.querySelector('.ur-actions');

    if (!payload) {
      // Direct visit with no order attached. Show the message, hide the printer.
      if (printerWrap) printerWrap.style.display = 'none';
      if (actions) actions.style.display = 'none';
      var tear0 = root.querySelector('.ur-tear');
      if (tear0) tear0.style.display = 'none';
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    var order = normalise(payload, defaults);

    var run = function () { print(root, order, { speed: speed }); };
    var replay = root.querySelector('[data-ur-replay]');
    if (replay) replay.addEventListener('click', run);

    run();
  }

  function boot() {
    document.querySelectorAll('[data-ur-receipt]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', boot);
})();
